import { useCallback, useEffect, useMemo, useState } from "react";
import { Activity, CalendarClock, Clock, PhoneOutgoing, UserCheck } from "lucide-react";
import { QueueList } from "@/components/dialer/QueueList";
import { ContactPanel } from "@/components/dialer/ContactPanel";
import { ControlsPanel } from "@/components/dialer/ControlsPanel";
import { LiveCalls } from "@/components/dialer/LiveCalls";
import { DispositionForm } from "@/components/dialer/DispositionForm";
import { useApp } from "@/context/AppContext";
import { api, fmtDT } from "@/lib/api";

const CFG_KEY = "pb_dialer_cfg";
const defaultCfg = { mode: "ai", source: "describe", fromNumber: "", scriptId: "", voiceId: "", briefing: { tone: "professional" } };

const Stat = ({ icon: Icon, k, v, testId }) => (
  <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 min-w-0" data-testid={testId}>
    <Icon className="h-4 w-4 text-slate-400 shrink-0" />
    <div className="min-w-0"><p className="text-[11px] text-slate-500">{k}</p><p className="text-sm font-semibold text-slate-900 truncate">{v}</p></div>
  </div>
);

export default function Dialer() {
  const { status } = useApp();
  const [q, setQ] = useState("");
  const [statusF, setStatusF] = useState("");
  const [contacts, setContacts] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [contact, setContact] = useState(null);
  const [ov, setOv] = useState(null);
  const [numbers, setNumbers] = useState([]);
  const [scripts, setScripts] = useState([]);
  const [voices, setVoices] = useState([]);
  const [cfg, setCfg] = useState(() => ({ ...defaultCfg, ...JSON.parse(localStorage.getItem(CFG_KEY) || "{}") }));

  useEffect(() => {
    localStorage.setItem(CFG_KEY, JSON.stringify(cfg));
  }, [cfg]);

  const loadContacts = useCallback(async () => {
    const { data } = await api.get("/contacts", { params: { q, status: statusF } });
    setContacts(data);
  }, [q, statusF]);
  const loadContact = useCallback(async () => {
    if (!selectedId) return setContact(null);
    const { data } = await api.get(`/contacts/${selectedId}`);
    setContact(data);
  }, [selectedId]);
  const loadOv = useCallback(async () => {
    const { data } = await api.get("/dialer/overview");
    setOv(data);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => loadContacts().catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [loadContacts]);
  useEffect(() => {
    loadContact().catch(() => {});
  }, [loadContact]);
  useEffect(() => {
    loadOv().catch(() => {});
    const t = setInterval(() => {
      loadOv().catch(() => {});
      loadContact().catch(() => {});
    }, 4000);
    return () => clearInterval(t);
  }, [loadOv, loadContact]);
  useEffect(() => {
    api.get("/numbers").then((r) => setNumbers(r.data.filter((n) => n.enabled))).catch(() => {});
    api.get("/scripts").then((r) => setScripts(r.data.scripts)).catch(() => {});
  }, []);
  useEffect(() => {
    if (status?.elevenlabs?.configured) api.get("/voices").then((r) => setVoices(r.data)).catch(() => {});
  }, [status]);

  const reloadAll = () => {
    loadOv().catch(() => {});
    loadContact().catch(() => {});
    loadContacts().catch(() => {});
  };
  const session = ov?.session;
  const sessionIds = useMemo(() => new Set(session?.contact_ids || []), [session]);
  const agents = ov?.agents || [];
  const avail = agents.filter((a) => a.availability === "available" && (a.online || a.call_target === "phone")).length;

  return (
    <div className="flex flex-col lg:h-[calc(100vh-3.5rem)] p-4 gap-3">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <Stat icon={Activity} k="Active calls" v={ov?.active_calls?.length ?? 0} testId="stat-active-calls" />
        <Stat icon={UserCheck} k="Agents available" v={`${avail} / ${agents.length}`} testId="stat-agents-available" />
        <Stat icon={PhoneOutgoing} k="Outbound number" v={cfg.fromNumber || (numbers[0]?.phone_number ? `${numbers[0].phone_number} (default)` : "None enabled")} testId="stat-outbound-number" />
        <Stat icon={CalendarClock} k="Scheduled callbacks" v={ov?.callbacks?.length ?? 0} testId="stat-callbacks" />
        <Stat icon={Clock} k="Next eligible attempt" v={session?.next_attempt_at ? fmtDT(session.next_attempt_at) : session ? session.waiting_reason : "No session"} testId="stat-next-attempt" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 flex-1 min-h-0">
        <QueueList contacts={contacts} selectedId={selectedId} onSelect={setSelectedId} q={q} setQ={setQ} status={statusF} setStatus={setStatusF} sessionIds={sessionIds} />
        <section className="lg:col-span-6 flex flex-col gap-4 bg-white border border-slate-200 rounded-lg p-5 overflow-y-auto scroll-thin min-h-0" data-testid="center-panel">
          {(ov?.pending_dispositions || []).map((c) => <DispositionForm key={c.id} call={c} onDone={reloadAll} />)}
          <ContactPanel contact={contact} reload={reloadAll} />
          <div className="border-t border-slate-200 pt-4">
            <h3 className="text-sm font-semibold text-slate-800 mb-2">Live calls</h3>
            <LiveCalls calls={ov?.active_calls || []} />
          </div>
          {ov?.callbacks?.length > 0 && (
            <div className="border-t border-slate-200 pt-4" data-testid="callbacks-list">
              <h3 className="text-sm font-semibold text-slate-800 mb-2">Scheduled callbacks</h3>
              {ov.callbacks.map((c) => (
                <button key={c.id} onClick={() => setSelectedId(c.id)} className="w-full flex justify-between text-xs py-1 hover:bg-slate-50 px-1 rounded">
                  <span>{c.name}</span><span className="text-slate-500">{fmtDT(c.callback_at)}</span>
                </button>
              ))}
            </div>
          )}
        </section>
        <ControlsPanel cfg={cfg} setCfg={setCfg} numbers={numbers} scripts={scripts} voices={voices} contact={contact} session={session} lastSession={ov?.last_session}
          queueIds={contacts.map((c) => c.id)} reload={reloadAll} onScriptSaved={(s) => setScripts((xs) => [...xs, s])} />
      </div>
    </div>
  );
}
